import { useMemo } from 'react';
import { useSelector } from 'react-redux';

import useAvailableMaterials from 'lib/hooks/useAvailableMaterials';
import {
  requirementsFromMaterials,
  findLpInRequirements,
} from 'lib/utils/pickingMaterials';
import { getPicksByWoo } from 'lib/selectors/getPicking';
import { getWooProducedParts } from 'lib/selectors/getWooProducedParts';
import { getStagedPicksToWrite } from 'lib/selectors/getStagedPicksToWrite';
import {
  LP_NUMBER_KEY,
  CONSUMPTION_QTY_KEY,
  ITEM_NUMBER_KEY,
  PICK_ID_KEY,
  LOCATION_KEY,
  PL_JOURNAL_ID_KEY,
} from 'lib/selectors/getPickingFieldConfig';

/**
 * Rebuilds pick rows from the WOO's durable plConsumptions entries. Newer
 * entries are self-describing (itemNumber/pickId/location written at submit);
 * older ones resolve their item via the LP in availableMaterials — an LP that
 * has since dropped out of availableMaterials (fully consumed) and carries no
 * itemNumber can't be attributed to a material and is skipped.
 */
const picksFromConsumptions = (plConsumptions, requirements) => {
  return (plConsumptions || []).reduce((memo, entry, index) => {
    const licensePlate = entry[LP_NUMBER_KEY];
    const resolved =
      entry[ITEM_NUMBER_KEY] && entry[LOCATION_KEY]
        ? null
        : findLpInRequirements(requirements, licensePlate);
    const itemNumber = entry[ITEM_NUMBER_KEY] || resolved?.itemNumber;
    if (!itemNumber) return memo;

    memo.push({
      id:
        entry[PICK_ID_KEY] ||
        entry[PL_JOURNAL_ID_KEY] ||
        `${licensePlate}-${index}`,
      licensePlate,
      quantity: entry[CONSUMPTION_QTY_KEY] || 0,
      itemNumber,
      location: entry[LOCATION_KEY] || resolved?.lpInfo?.location || null,
      status: 'submitted',
      // The connector stamps plJournalId once the pick is posted to D365.
      posted: Boolean(entry[PL_JOURNAL_ID_KEY]),
    });
    return memo;
  }, []);
};

/**
 * Joins the WOO's required materials (from the availableMaterials custom field,
 * synced by the dynamics365-connector) with its picks — submitted ones rebuilt
 * from the durable plConsumptions custom field, pending ones from Redux — and
 * the produced-parts count to produce per-material rows for the picking UI.
 *
 * `wooId` is the WOO's real `workOrderOperationRef`; picks are stored and read
 * under that key. A WOO whose availableMaterials custom field has not been
 * synced yields no materials, which blocks the picking flow.
 *
 * `partsProduced` is the current WOO's committed labor-ticket total (good + bad)
 * via getWooProducedParts — the same basis the submission gate uses, so the log
 * and the gate cannot disagree. `wooId` is assumed to be the current WOO (the
 * only WOO picking runs for).
 *
 * For each required material:
 *   pickedQty  = sum of pick quantities for that item
 *   consumed   = partsProduced * qtyPerUnit
 *   remaining  = pickedQty - consumed   (picked-but-not-consumed)
 *
 * Each LP also gets `available`: its synced on-hand minus this WOO's picks from it
 * that are not posted yet. The sync reflects a pick only after it posts, so without
 * this an operator could pick the same on-hand twice before the next sync. Posted
 * picks are not subtracted, since the sync may already include them.
 */
const usePickingSummary = (wooId) => {
  const picksByWoo = useSelector(getPicksByWoo);
  const partsProduced = useSelector(getWooProducedParts);
  const stagedToWrite = useSelector(getStagedPicksToWrite);
  const {
    materials: availableMaterials,
    plConsumptions,
    loading,
    loaded,
    error,
    refetch,
  } = useAvailableMaterials(wooId);

  return useMemo(() => {
    const requirements = requirementsFromMaterials(availableMaterials);

    // Submitted picks come from the WOO's durable plConsumptions record (so
    // they survive a reload); Redux supplies the rest — pending picks and any
    // just-submitted picks the cached customFields read doesn't include yet.
    // pickId ties the two sources together so a pick never renders twice.
    const submittedPicks = picksFromConsumptions(plConsumptions, requirements);
    const submittedIds = new Set(
      submittedPicks.map((pick) => {
        return pick.id;
      })
    );
    // Staged picks count only once the job start they were made for is
    // confirmed. Before that the start may still fail or be abandoned, so they
    // must not count as picked material. useFlushStagedPicks then marks them
    // 'writing', which counts like any other unwritten pick; this covers the
    // render in between.
    const confirmedStagedIds = new Set(
      String(stagedToWrite?.workOrderOperationRef) === String(wooId)
        ? stagedToWrite.picks.map((pick) => {
            return pick.id;
          })
        : []
    );
    const reduxPicks = (picksByWoo[wooId] || []).filter((pick) => {
      if (submittedIds.has(pick.id)) return false;
      return pick.status !== 'staged' || confirmedStagedIds.has(pick.id);
    });
    const picks = [...submittedPicks, ...reduxPicks];

    const pickedByItem = picks.reduce((memo, pick) => {
      return {
        ...memo,
        [pick.itemNumber]: (memo[pick.itemNumber] || 0) + pick.quantity,
      };
    }, {});

    const unpostedByLp = picks.reduce((memo, pick) => {
      if (pick.posted) return memo;
      const key = `${pick.itemNumber}:${pick.licensePlate}`;
      return { ...memo, [key]: (memo[key] || 0) + pick.quantity };
    }, {});

    const materials = requirements.map((req) => {
      const pickedQty = pickedByItem[req.itemNumber] || 0;
      const lps = req.lps.map((lp) => {
        const unposted = unpostedByLp[`${req.itemNumber}:${lp.lp}`] || 0;
        return { ...lp, available: Math.max((lp.onHand || 0) - unposted, 0) };
      });
      const consumed = (req.qtyPerUnit || 0) * partsProduced;
      const remaining = pickedQty - consumed;
      const itemPicks = picks.filter((pick) => {
        return pick.itemNumber === req.itemNumber;
      });
      return {
        ...req,
        lps,
        pickedQty,
        consumed,
        remaining,
        picks: itemPicks,
        isShort: pickedQty < req.requiredQty,
      };
    });

    return {
      materials,
      picks,
      partsProduced,
      loading,
      loaded,
      error,
      refetch,
    };
  }, [
    wooId,
    availableMaterials,
    plConsumptions,
    loading,
    loaded,
    error,
    refetch,
    picksByWoo,
    stagedToWrite,
    partsProduced,
  ]);
};

export default usePickingSummary;
